import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Select, MenuItem,
  FormControl, InputLabel, TextField, Table, TableBody, TableCell,
  TableHead, TableRow, TableContainer, Paper, Grid, Alert,
  CircularProgress, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import axiosInstance from '../../api/axiosConfig';

const Results = () => {
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [examName, setExamName] = useState('');
  const [examDate, setExamDate] = useState('');

  const [marks, setMarks] = useState({});
  const [absent, setAbsent] = useState({});
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
  try {
    const [studentsRes, coursesRes] = await Promise.all([
      axiosInstance.get('/students/search?pageSize=1000'),
      axiosInstance.get('/courses')
    ]);

    // ✅ FIX FOR students.map ERROR
    const studentsData = studentsRes.data.students || studentsRes.data.data || studentsRes.data;
    setStudents(Array.isArray(studentsData) ? studentsData : []);

    const coursesData = coursesRes.data.data || coursesRes.data;
    setCourses(Array.isArray(coursesData) ? coursesData : []);

  } catch (err) {
    console.error(err);
    alert('Failed to load students or courses');
  } finally {
    setLoading(false);
  }
};


  const fetchSubjects = async (courseId) => {
  if (!courseId) return;

  try {
    const response = await axiosInstance.get(`/subjects/by-course/${courseId}`);
    const data = response.data.data || response.data;
    setSubjects(Array.isArray(data) ? data : []);
  } catch (err) {
    console.error('Error fetching subjects:', err);
    setSubjects([]);
    alert('Failed to load subjects');
  }
};


  const handleStudentChange = async (studentId) => {
  setSelectedStudent(studentId);

  try {
    // 🔥 CALL FULL STUDENT DETAILS API
    const res = await axiosInstance.get(`/students/${studentId}`);
    const student = res.data;

    const courseId = student.courseId;

    if (!courseId) {
      alert("Selected student has no course assigned");
      return;
    }

    setSelectedCourse(courseId);
    await fetchSubjects(courseId);

  } catch (err) {
    console.error("Failed to fetch student details", err);
    alert("Failed to load student course");
  }
};



  const handleMarksChange = (subjectId, value) => {
    setMarks(prev => ({ ...prev, [subjectId]: value }));
  };

  const handleAbsentChange = (subjectId, value) => {
    setAbsent(prev => ({ ...prev, [subjectId]: value }));
    if (value) handleMarksChange(subjectId, 0);
  };

  const calculateTotal = () =>
    subjects.reduce((sum, s) => sum + Number(marks[s.subjectId] || 0), 0);

  const calculateMax = () =>
    subjects.reduce((sum, s) => sum + s.maxMarks, 0);

  const handleSubmit = async () => {
    if (!selectedStudent || !examName || !examDate) {
      alert('Fill all required fields');
      return;
    }

    const subjectResults = subjects.map(s => ({
      subjectId: s.subjectId,
      marksObtained: absent[s.subjectId] ? 0 : Number(marks[s.subjectId] || 0),
      isAbsent: absent[s.subjectId] || false
    }));

    const payload = {
      studentId: selectedStudent,
      examName,
      examDate,
      subjectResults
    };

    try {
      setSubmitting(true);
      await axiosInstance.post('/results', payload);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error(err);
      alert('Failed to submit results');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" mb={4}>Result Entry</Typography>

      {success && <Alert severity="success">Results saved successfully!</Alert>}

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Student</InputLabel>
                <Select value={selectedStudent} onChange={e => handleStudentChange(e.target.value)}>
                  {students.map(s => (
  <MenuItem key={s.studentId} value={s.studentId}>
    {s.fullName}
  </MenuItem>
))}

                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Exam Name" value={examName} onChange={e => setExamName(e.target.value)} />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField fullWidth type="date" label="Exam Date" InputLabelProps={{ shrink: true }} value={examDate} onChange={e => setExamDate(e.target.value)} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {subjects.length > 0 && (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Subject</TableCell>
                  <TableCell>Max</TableCell>
                  <TableCell>Min</TableCell>
                  <TableCell>Marks</TableCell>
                  <TableCell>Absent</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subjects.map(s => (
                  <TableRow key={s.subjectId}>
                    <TableCell>{s.subjectName}</TableCell>
                    <TableCell>{s.maxMarks}</TableCell>
                    <TableCell>{s.minMarks}</TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={marks[s.subjectId]}
                        onChange={e => handleMarksChange(s.subjectId, e.target.value)}
                        disabled={absent[s.subjectId]}
                        inputProps={{ min: 0, max: s.maxMarks }}
                      />
                    </TableCell>
                    <TableCell>
                      <Checkbox
                        checked={absent[s.subjectId]}
                        onChange={e => handleAbsentChange(s.subjectId, e.target.checked)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell><b>Total</b></TableCell>
                  <TableCell><b>{calculateMax()}</b></TableCell>
                  <TableCell />
                  <TableCell><b>{calculateTotal()}</b></TableCell>
                  <TableCell>
                    <b>{((calculateTotal() / calculateMax()) * 100 || 0).toFixed(1)}%</b>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Button variant="contained" sx={{ mt: 3 }} onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : 'Submit Results'}
          </Button>
        </>
      )}
    </Box>
  );
};

export default Results;
